import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';

const TABS = ['Overview', 'Database', 'Users', 'Subscription', 'Audit'];

export default function TenantDetail() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [tenant, setTenant] = useState(null);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('Overview');
  const [connResult, setConnResult] = useState(null);
  const [resetModal, setResetModal] = useState(null);
  const [newPwd, setNewPwd] = useState('');
  const [resetMsg, setResetMsg] = useState('');
  const [deleteModal, setDeleteModal] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [toast, setToast] = useState(null);

  const fetchAll = useCallback(async () => {
    try {
      const [tRes, sRes] = await Promise.all([
        api.get(`/tenants/${code}`),
        api.get(`/tenants/${code}/stats`),
      ]);
      setTenant(tRes.data.data);
      setStats(sRes.data.data);
    } catch {} finally { setLoading(false); }
  }, [code]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const loadUsers = async () => {
    try { const { data } = await api.get(`/tenants/${code}/users`); setUsers(data.data || []); } catch {}
  };
  const loadAudit = async () => {
    try { const { data } = await api.get(`/tenants/${code}/audit?limit=100`); setAudit(data.data || []); } catch {}
  };

  useEffect(() => { if (tab === 'Users') loadUsers(); if (tab === 'Audit') loadAudit(); }, [tab]); // eslint-disable-line

  const testConnection = async () => {
    setConnResult({ loading: true });
    try {
      const { data } = await api.post(`/tenants/${code}/test-connection`);
      setConnResult(data.data);
    } catch { setConnResult({ connected: false, error: 'Request failed' }); }
  };

  const handleResetPwd = async () => {
    if (!newPwd || newPwd.length < 6) { setResetMsg('Min 6 characters'); return; }
    try {
      await api.post(`/tenants/${code}/reset-credentials`, { user_id: resetModal.id, new_password: newPwd });
      setResetMsg('Password reset successfully!');
      setTimeout(() => { setResetModal(null); setNewPwd(''); setResetMsg(''); }, 1500);
    } catch (e) { setResetMsg(e.response?.data?.message || 'Failed'); }
  };

  const toggleStatus = async (newStatus) => {
    if (!window.confirm(`Change status to "${newStatus}"?`)) return;
    try { await api.patch(`/tenants/${code}/status`, { status: newStatus }); fetchAll(); } catch {}
  };

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const openDeleteModal = () => setDeleteModal({ code: tenant.code, name: tenant.name, mode: 'soft' });

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/tenants/${deleteModal.code}?mode=${deleteModal.mode}`);
      setDeleteModal(null);
      if (deleteModal.mode === 'hard') {
        showToast(`Tenant "${deleteModal.name}" permanently deleted`, 'success');
        setTimeout(() => navigate('/tenants'), 1500);
      } else {
        showToast(`Tenant "${deleteModal.name}" soft-deleted`, 'success');
        fetchAll();
      }
    } catch (e) {
      showToast(e.response?.data?.message || 'Delete failed', 'error');
    } finally { setDeleting(false); }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>;
  if (!tenant) return <div className="text-center text-gray-500 py-10">Tenant not found</div>;

  const db = stats?.database || {};
  const t = stats?.tenant || tenant;
  const STATUS_COLORS = { active: 'bg-green-100 text-green-700 border-green-200', suspended: 'bg-amber-100 text-amber-700 border-amber-200', disabled: 'bg-gray-100 text-gray-600 border-gray-200', deleted: 'bg-red-100 text-red-700 border-red-200' };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center space-x-4">
          <button onClick={() => navigate('/tenants')} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
          </button>
          <div>
            <div className="flex items-center space-x-3">
              <h2 className="text-xl font-bold text-gray-900">{tenant.name}</h2>
              <span className={`text-xs px-2.5 py-1 rounded-full font-medium border ${STATUS_COLORS[tenant.status]}`}>{tenant.status}</span>
            </div>
            <p className="text-sm text-gray-500 mt-0.5">Code: <span className="font-mono bg-gray-100 px-1.5 py-0.5 rounded text-xs">{tenant.code}</span> &middot; Registered {new Date(tenant.created_at).toLocaleDateString()}</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          {tenant.status === 'active' && <button onClick={() => toggleStatus('suspended')} className="px-3 py-2 text-sm text-amber-700 bg-amber-50 hover:bg-amber-100 rounded-lg transition-colors">Suspend</button>}
          {tenant.status === 'suspended' && <button onClick={() => toggleStatus('active')} className="px-3 py-2 text-sm text-green-700 bg-green-50 hover:bg-green-100 rounded-lg transition-colors">Activate</button>}
          {tenant.status === 'disabled' && <button onClick={() => toggleStatus('active')} className="px-3 py-2 text-sm text-green-700 bg-green-50 hover:bg-green-100 rounded-lg transition-colors">Re-activate</button>}
          {tenant.status === 'deleted' && <button onClick={() => toggleStatus('active')} className="px-3 py-2 text-sm text-green-700 bg-green-50 hover:bg-green-100 rounded-lg transition-colors">Restore</button>}
          {tenant.status !== 'deleted' && <button onClick={openDeleteModal} className="px-3 py-2 text-sm text-red-700 bg-red-50 hover:bg-red-100 rounded-lg transition-colors">Delete</button>}
          <button onClick={() => navigate(`/tenants/${code}/edit`)} className="px-3 py-2 text-sm text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors">Edit</button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <div className="flex space-x-6">
          {TABS.map(t => (
            <button key={t} onClick={() => setTab(t)} className={`pb-3 text-sm font-medium border-b-2 transition-colors ${tab === t ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>{t}</button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      {tab === 'Overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Org Info */}
          <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm">
            <h3 className="text-sm font-semibold text-gray-900 mb-4">Organization Info</h3>
            <dl className="space-y-3">
              {[['Name', tenant.name], ['Code', tenant.code], ['Admin Email', tenant.admin_email || '—'], ['Admin Phone', tenant.admin_phone || '—'], ['Address', [tenant.address, tenant.city, tenant.state].filter(Boolean).join(', ') || '—'], ['Max Users', tenant.max_users], ['Created', new Date(tenant.created_at).toLocaleString()]].map(([k, v]) => (
                <div key={k} className="flex justify-between"><dt className="text-sm text-gray-500">{k}</dt><dd className="text-sm font-medium text-gray-900 text-right">{v}</dd></div>
              ))}
            </dl>
          </div>
          {/* DB Stats */}
          <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm">
            <h3 className="text-sm font-semibold text-gray-900 mb-4">Database Summary</h3>
            {db.connected === false ? (
              <div className="text-red-600 text-sm">{db.error || 'Cannot connect'}</div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                {[['Users', db.users], ['Active Users', db.active_users], ['Schemes', db.schemes], ['Vouchers', db.vouchers], ['Tables', db.tables], ['Size', `${db.database_size_mb} MB`]].map(([k, v]) => (
                  <div key={k} className="bg-gray-50 rounded-lg p-3"><div className="text-xs text-gray-500">{k}</div><div className="text-lg font-bold text-gray-900">{v ?? '—'}</div></div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {tab === 'Database' && (
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm">
            <h3 className="text-sm font-semibold text-gray-900 mb-4">Connection Details</h3>
            <dl className="grid grid-cols-2 gap-4">
              {[['Host', tenant.db_host], ['Port', tenant.db_port], ['Database', tenant.db_name], ['User', tenant.db_user], ['Password', tenant.db_password_masked]].map(([k, v]) => (
                <div key={k}><dt className="text-xs text-gray-500">{k}</dt><dd className="text-sm font-mono font-medium text-gray-900 mt-0.5">{v}</dd></div>
              ))}
            </dl>
            <button onClick={testConnection} className="mt-4 px-4 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors">Test Connection</button>
            {connResult && !connResult.loading && (
              <div className={`mt-3 p-3 rounded-lg text-sm ${connResult.connected ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                {connResult.connected ? `Connected - ${connResult.tables} tables, ${connResult.users} users, MySQL ${connResult.mysql_version}` : `Failed: ${connResult.error}`}
              </div>
            )}
            {connResult?.loading && <div className="mt-3 text-sm text-gray-500">Testing...</div>}
          </div>
        </div>
      )}

      {tab === 'Users' && (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="p-4 border-b flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900">Tenant Users ({users.length})</h3>
          </div>
          {users.length === 0 ? (
            <div className="text-center py-8 text-gray-400 text-sm">No users found or DB unreachable</div>
          ) : (
            <table className="w-full">
              <thead><tr className="bg-gray-50 border-b text-xs font-semibold text-gray-500 uppercase">
                {['ID', 'Username', 'Full Name', 'Email', 'Role ID', 'Status', 'Last Login', 'Actions'].map(h => <th key={h} className="px-4 py-2.5 text-left">{h}</th>)}
              </tr></thead>
              <tbody className="divide-y divide-gray-50">
                {users.map(u => (
                  <tr key={u.id} className="hover:bg-gray-50/50">
                    <td className="px-4 py-2.5 text-sm">{u.id}</td>
                    <td className="px-4 py-2.5 text-sm font-medium">{u.username}</td>
                    <td className="px-4 py-2.5 text-sm">{u.full_name || '—'}</td>
                    <td className="px-4 py-2.5 text-sm text-gray-500">{u.email || '—'}</td>
                    <td className="px-4 py-2.5 text-sm">{u.role_id}</td>
                    <td className="px-4 py-2.5"><span className={`text-xs px-2 py-0.5 rounded-full ${u.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{u.status || 'active'}</span></td>
                    <td className="px-4 py-2.5 text-xs text-gray-400">{u.last_login ? new Date(u.last_login).toLocaleString() : 'Never'}</td>
                    <td className="px-4 py-2.5">
                      <button onClick={() => { setResetModal(u); setNewPwd(''); setResetMsg(''); }} className="text-xs text-blue-600 hover:text-blue-700 font-medium">Reset Password</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {tab === 'Subscription' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm">
            <h3 className="text-sm font-semibold text-gray-900 mb-4">Subscription Details</h3>
            <dl className="space-y-3">
              {[
                ['Plan', <span className="capitalize">{tenant.subscription_plan}</span>],
                ['Start Date', tenant.subscription_start ? new Date(tenant.subscription_start).toLocaleDateString() : '—'],
                ['Expiry Date', tenant.subscription_expiry ? new Date(tenant.subscription_expiry).toLocaleDateString() : '—'],
                ['Days Remaining', tenant.days_until_expiry != null ? (
                  <span className={tenant.days_until_expiry < 0 ? 'text-red-600 font-bold' : tenant.days_until_expiry <= 30 ? 'text-amber-600 font-bold' : 'text-green-600 font-bold'}>
                    {tenant.days_until_expiry < 0 ? `Expired ${Math.abs(tenant.days_until_expiry)} days ago` : `${tenant.days_until_expiry} days`}
                  </span>
                ) : '—'],
                ['Amount', tenant.subscription_amount ? `₹${parseFloat(tenant.subscription_amount).toLocaleString()}` : '—'],
                ['Billing Cycle', <span className="capitalize">{tenant.billing_cycle || '—'}</span>],
                ['Auto Renew', tenant.auto_renew ? 'Yes' : 'No'],
                ['Last Payment', tenant.last_payment_date ? new Date(tenant.last_payment_date).toLocaleDateString() : '—'],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between items-center"><dt className="text-sm text-gray-500">{k}</dt><dd className="text-sm font-medium text-gray-900">{v}</dd></div>
              ))}
            </dl>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm">
            <h3 className="text-sm font-semibold text-gray-900 mb-4">Quick Actions</h3>
            <div className="space-y-3">
              <button onClick={() => navigate(`/tenants/${code}/edit`)} className="w-full text-left px-4 py-3 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg text-sm font-medium transition-colors">Edit Subscription Details</button>
              <button onClick={() => navigate('/subscriptions')} className="w-full text-left px-4 py-3 bg-green-50 hover:bg-green-100 text-green-700 rounded-lg text-sm font-medium transition-colors">Subscription Management</button>
            </div>
          </div>
        </div>
      )}

      {tab === 'Audit' && (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="p-4 border-b"><h3 className="text-sm font-semibold text-gray-900">Audit Trail ({audit.length} records)</h3></div>
          {audit.length === 0 ? (
            <div className="text-center py-8 text-gray-400 text-sm">No audit records</div>
          ) : (
            <div className="divide-y divide-gray-50">
              {audit.map(a => (
                <div key={a.id} className="px-4 py-3 flex items-center space-x-3">
                  <div className={`w-2 h-2 rounded-full flex-shrink-0 ${a.action?.includes('CREATE') ? 'bg-green-500' : a.action?.includes('DELETE') ? 'bg-red-500' : 'bg-blue-500'}`} />
                  <div className="flex-1"><span className="text-sm font-medium text-gray-900">{a.action?.replace(/_/g, ' ')}</span></div>
                  <span className="text-xs text-gray-500">{a.performed_by_name}</span>
                  <span className="text-xs text-gray-400">{new Date(a.created_at).toLocaleString()}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Reset Password Modal */}
      {resetModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-xl">
            <h3 className="text-lg font-bold text-gray-900">Reset Password</h3>
            <p className="text-sm text-gray-500 mt-1">User: <strong>{resetModal.username}</strong> (ID: {resetModal.id})</p>
            <input type="password" value={newPwd} onChange={e => setNewPwd(e.target.value)} placeholder="New password (min 6 chars)" className="w-full mt-4 px-3 py-2.5 border border-gray-300 rounded-lg text-sm" />
            {resetMsg && <div className={`mt-2 text-sm ${resetMsg.includes('success') ? 'text-green-600' : 'text-red-600'}`}>{resetMsg}</div>}
            <div className="flex justify-end space-x-3 mt-4">
              <button onClick={() => setResetModal(null)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">Cancel</button>
              <button onClick={handleResetPwd} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">Reset</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => !deleting && setDeleteModal(null)}>
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="p-5 border-b border-gray-100">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" /></svg>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Delete Tenant</h3>
                  <p className="text-sm text-gray-500">{deleteModal.name} ({deleteModal.code})</p>
                </div>
              </div>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-sm text-gray-600 mb-4">Choose how to delete this tenant:</p>
              <label onClick={() => setDeleteModal(m => ({...m, mode: 'soft'}))} className={`block p-4 rounded-xl border-2 cursor-pointer transition-all ${deleteModal.mode === 'soft' ? 'border-amber-400 bg-amber-50' : 'border-gray-200 hover:border-gray-300'}`}>
                <div className="flex items-center space-x-3">
                  <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${deleteModal.mode === 'soft' ? 'border-amber-500' : 'border-gray-300'}`}>
                    {deleteModal.mode === 'soft' && <div className="w-2 h-2 rounded-full bg-amber-500" />}
                  </div>
                  <div>
                    <span className="font-semibold text-gray-900">Soft Delete</span>
                    <span className="ml-2 text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">Reversible</span>
                  </div>
                </div>
                <p className="text-sm text-gray-500 mt-2 ml-7">Mark as deleted. Database preserved. Can be restored later.</p>
              </label>
              <label onClick={() => setDeleteModal(m => ({...m, mode: 'hard'}))} className={`block p-4 rounded-xl border-2 cursor-pointer transition-all ${deleteModal.mode === 'hard' ? 'border-red-400 bg-red-50' : 'border-gray-200 hover:border-gray-300'}`}>
                <div className="flex items-center space-x-3">
                  <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${deleteModal.mode === 'hard' ? 'border-red-500' : 'border-gray-300'}`}>
                    {deleteModal.mode === 'hard' && <div className="w-2 h-2 rounded-full bg-red-500" />}
                  </div>
                  <div>
                    <span className="font-semibold text-gray-900">Hard Delete</span>
                    <span className="ml-2 text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">Permanent</span>
                  </div>
                </div>
                <p className="text-sm text-gray-500 mt-2 ml-7">Permanently DROP the database and remove all records. Cannot be undone.</p>
              </label>
              {deleteModal.mode === 'hard' && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 mt-3">
                  <p className="text-sm text-red-700 font-medium">⚠ This will DROP the database <span className="font-mono">tenant_{deleteModal.code}_db</span> and permanently erase all tenant data.</p>
                </div>
              )}
            </div>
            <div className="p-5 border-t border-gray-100 flex justify-end space-x-3">
              <button onClick={() => setDeleteModal(null)} disabled={deleting} className="px-4 py-2.5 text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors">Cancel</button>
              <button onClick={handleDelete} disabled={deleting} className={`px-4 py-2.5 text-sm text-white rounded-lg transition-colors flex items-center space-x-2 ${deleteModal.mode === 'hard' ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-600 hover:bg-amber-700'}`}>
                {deleting && <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>}
                <span>{deleting ? 'Deleting...' : deleteModal.mode === 'hard' ? 'Permanently Delete' : 'Soft Delete'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-6 right-6 z-50 px-5 py-3 rounded-xl shadow-lg text-sm font-medium text-white transition-all ${toast.type === 'error' ? 'bg-red-600' : 'bg-green-600'}`}>
          {toast.message}
        </div>
      )}
    </div>
  );
}
